import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { CanvasBlock } from '@/lib/cms/card-registry'

/* ============================================================
   useCanvasLayout — Load & Save canvas blocks from DB
   Stores blocks as JSONB in canvas_layouts table.
   ============================================================ */

/** Serializable block shape (strips runtime entityData) */
interface SerializedBlock {
  id: string
  type: string
  colSpan: number
  data: Record<string, string>
  entityId?: string
}

function stripEntityData(blocks: CanvasBlock[]): SerializedBlock[] {
  return blocks.map(({ entityData, ...rest }) => {
    void entityData
    return rest
  })
}

/** Read canvas layout for a tenant + page */
export function useCanvasLayout(tenantId: string | undefined, pageKey = 'home') {
  return useQuery({
    queryKey: ['canvas-layout', tenantId, pageKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('canvas_layouts')
        .select('blocks')
        .eq('tenant_id', tenantId!)
        .eq('page_key', pageKey)
        .maybeSingle()

      if (error) throw error
      if (!data) return null
      const row = data as Record<string, unknown>
      return (row.blocks ?? []) as CanvasBlock[]
    },
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
  })
}

/** Save (upsert) canvas layout */
export function useSaveCanvasLayout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      tenantId,
      pageKey = 'home',
      blocks,
    }: {
      tenantId: string
      pageKey?: string
      blocks: CanvasBlock[]
    }) => {
      const serialized = stripEntityData(blocks)

      const { error } = await supabase.from('canvas_layouts')
        .upsert(
          {
            tenant_id: tenantId,
            page_key: pageKey,
            blocks: serialized,
          } as never,
          { onConflict: 'tenant_id,page_key' },
        )

      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['canvas-layout'] })
    },
  })
}
